/**
 * The NTP four-timestamp offset estimate (D9, docs/04 §9.6).
 *
 * Pure arithmetic over samples someone else collected — no I/O, no clock, no
 * state — so the filtering rules are testable directly against the numbers that
 * would otherwise only appear on a bad mobile link.
 */

export interface TimeSample {
  /** Client send time, `Date.now()` at request. */
  t0: number
  /** `server_recv_ms` from the response. */
  t1: number
  /** `server_time_ms` from the response. */
  t2: number
  /** Client receive time, `Date.now()` at response. */
  t3: number
}

/** `θ = ((t1 − t0) + (t2 − t3)) / 2` */
export function offsetOf(s: TimeSample): number {
  return (s.t1 - s.t0 + (s.t2 - s.t3)) / 2
}

/** `δ = (t3 − t0) − (t2 − t1)` — round trip minus server processing. */
export function rttOf(s: TimeSample): number {
  return s.t3 - s.t0 - (s.t2 - s.t1)
}

/** Any sample slower than this is discarded outright (§9.6 filter 2). */
export const MAX_RTT_MS = 2000
/** Fewer surviving samples than this and the estimate is not trustworthy. */
export const MIN_SAMPLES = 3
/** Above this the countdown snaps and says so; below it, it slews. */
export const DRIFT_SNAP_THRESHOLD_MS = 250
/** A countdown that jumps backwards costs more trust than being 200ms wrong. */
export const MAX_SLEW_RATE_MS_PER_S = 50

export type SampleRejection = 'ASYMMETRIC_PATH' | 'RTT_TOO_HIGH' | 'MALFORMED'

export interface FilteredSamples {
  accepted: TimeSample[]
  rejected: Array<{ sample: TimeSample; reason: SampleRejection }>
  minRttMs: number | null
}

/**
 * Filtering, in the order §9.6 specifies. Order matters: the asymmetry test is
 * relative to the *set's* minimum, so applying the absolute cap first would
 * change which samples define that minimum.
 */
export function filterSamples(samples: readonly TimeSample[]): FilteredSamples {
  const rejected: FilteredSamples['rejected'] = []
  const wellFormed: Array<{ sample: TimeSample; rtt: number }> = []

  for (const sample of samples) {
    const rtt = rttOf(sample)
    // A negative round trip means the two clocks disagree about causality, or
    // a timestamp is missing. Either way there is nothing to estimate from.
    if (!Number.isFinite(rtt) || rtt < 0) {
      rejected.push({ sample, reason: 'MALFORMED' })
      continue
    }
    wellFormed.push({ sample, rtt })
  }

  if (wellFormed.length === 0) return { accepted: [], rejected, minRttMs: null }

  const minRtt = Math.min(...wellFormed.map((w) => w.rtt))
  const accepted: TimeSample[] = []
  for (const { sample, rtt } of wellFormed) {
    // An outlier delay is an asymmetric path, and its offset is biased by half
    // the excess — the error is systematic, not noise, so it cannot be averaged out.
    if (rtt > 2 * minRtt) {
      rejected.push({ sample, reason: 'ASYMMETRIC_PATH' })
      continue
    }
    if (rtt > MAX_RTT_MS) {
      rejected.push({ sample, reason: 'RTT_TOO_HIGH' })
      continue
    }
    accepted.push(sample)
  }

  return { accepted, rejected, minRttMs: minRtt }
}

/** Median, never mean: one bad sample must not move the answer. */
export function median(values: readonly number[]): number {
  if (values.length === 0) throw new Error('median of an empty set')
  const sorted = [...values].sort((a, b) => a - b)
  const mid = sorted.length >> 1
  if (sorted.length % 2 === 1) return sorted[mid] as number
  return ((sorted[mid - 1] as number) + (sorted[mid] as number)) / 2
}

export interface OffsetEstimate {
  offsetMs: number
  /** The median RTT of the surviving samples — the estimate's own error bar. */
  rttMs: number
  acceptedCount: number
  sampleCount: number
}

/**
 * `null` when fewer than `MIN_SAMPLES` survive. The caller re-samples once and,
 * failing that, runs at `θ = 0` behind a **"clock unverified"** indicator —
 * never behind a countdown the user might trust.
 */
export function estimateOffset(samples: readonly TimeSample[]): OffsetEstimate | null {
  const { accepted } = filterSamples(samples)
  if (accepted.length < MIN_SAMPLES) return null
  return {
    offsetMs: median(accepted.map(offsetOf)),
    rttMs: median(accepted.map(rttOf)),
    acceptedCount: accepted.length,
    sampleCount: samples.length,
  }
}
